import type { components } from './api.generated';
import { randomId } from './randomId';

type ChatResult = components['schemas']['ChatResult'];
interface Pending {
  readonly id: string;
  readonly resolve: () => void;
  readonly reject: (reason: Error) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

export function chatText(text: string): string {
  const trimmed = text.trim();
  if (!trimmed || chatLength(trimmed) > 80 || /[\p{C}\u2028\u2029]/u.test(trimmed))
    throw new Error('Write one line of up to 80 characters.');
  return trimmed;
}

/*
 * DRF's limit counts Unicode code points. Matching it keeps non-ASCII speech
 * within the server contract rather than counting UTF-16 surrogate halves.
 */
export const chatLength = (text: string): number => Array.from(text).length;

export class ChatSender {
  private pending: Pending | null = null;

  constructor(private readonly connection: () => WebSocket | null) {}

  send(text: string, recipient: string | null = null): Promise<void> {
    const value = chatText(text);
    const socket = this.connection();
    if (!socket || socket.readyState !== WebSocket.OPEN)
      return Promise.reject(new Error('The city is reconnecting. Your words have not been sent.'));
    if (this.pending)
      return Promise.reject(new Error('Wait for your last message to reach the city.'));
    const id = randomId();
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.fail('The city did not acknowledge your message.');
      }, 5000);
      this.pending = { id, resolve, reject, timer };
      const request: components['schemas']['ChatRequest'] = {
        type: 'chat',
        request_id: id,
        text: value,
        recipient,
      };
      socket.send(JSON.stringify(request));
    });
  }

  receive(result: ChatResult): void {
    const pending = this.pending;
    if (!pending || pending.id !== result.request_id) return;
    clearTimeout(pending.timer);
    this.pending = null;
    if (result.status === 'sent') pending.resolve();
    else
      pending.reject(
        new Error(
          result.status === 'wait'
            ? 'Allow a moment between messages.'
            : 'This visitor has left the city.'
        )
      );
  }

  fail(message = 'The connection closed before your message was confirmed.'): void {
    if (!this.pending) return;
    clearTimeout(this.pending.timer);
    this.pending.reject(new Error(message));
    this.pending = null;
  }
}
